'use client'

import { useState, useEffect } from 'react'
import { FileText, LogOut, Eye, User } from 'lucide-react'
import BriefView from './BriefView'
import { Language } from '@/lib/brief613/translations'
import { panel, panelInner, headingClass, mutedClass, ghostBtn } from './ui'

interface Manager {
  id: string
  name: string
  pronom: string | null
  email: string
  phone: string | null
  photoUrl: string | null
  username: string
}

interface Brief {
  id: string
  objective: string | null
  targetAudience: string | null
  desiredReaction: string | null
  keyMessage: string | null
  tone: string | null
  status: string
  createdAt: string
  reference?: string | null
  companyProfile: {
    companyName: string
    industry: string
  } | null
  user: {
    username: string
    email: string
    companyName: string | null
  }
}

interface ManagerDashboardProps {
  manager: Manager
  onLogout: () => void
  language?: Language
}

export default function ManagerDashboard({ manager, onLogout }: ManagerDashboardProps) {
  const [briefs, setBriefs] = useState<Brief[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedBriefId, setSelectedBriefId] = useState<string | null>(null)

  useEffect(() => {
    loadBriefs()
  }, [manager.id])

  const loadBriefs = async () => {
    try {
      const response = await fetch('/api/brief613/briefs/manager', {
        headers: {
          'x-manager-id': manager.id,
        },
      })

      const data = await response.json()

      if (data.success) {
        setBriefs(data.briefs)
      }
    } catch (error) {
      console.error('Error loading briefs:', error)
    } finally {
      setLoading(false)
    }
  }

  if (selectedBriefId) {
    return (
      <div className="max-w-6xl mx-auto">
        <BriefView
          briefId={selectedBriefId}
          userId={manager.id}
          onBack={() => {
            setSelectedBriefId(null)
            loadBriefs()
          }}
        />
      </div>
    )
  }

  if (loading) {
    return (
      <div className={`${panel} p-8 max-w-6xl mx-auto text-center`}>
        <div className="w-12 h-12 border-4 border-white/20 border-t-white/80 rounded-full animate-spin mx-auto mb-4" />
        <p className={mutedClass}>Loading dashboard...</p>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className={`${panel} p-6`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {manager.photoUrl ? (
              <img
                src={manager.photoUrl}
                alt={manager.name}
                className="w-12 h-12 rounded-full object-cover border border-white/10"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-white/[0.03] flex items-center justify-center border border-white/10">
                <User size={20} className="text-white/50" />
              </div>
            )}
            <div>
              <h2 className={headingClass}>
                {manager.pronom ? `${manager.pronom} ` : ''}{manager.name}
              </h2>
              <p className={`${mutedClass} mt-1`}>Project Manager Dashboard</p>
            </div>
          </div>
          <button
            onClick={onLogout}
            className={ghostBtn}
          >
            <LogOut size={14} />
            Logout
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className={`${panel} p-6`}>
          <p className="text-xl font-semibold text-white/90">{briefs.length}</p>
          <p className="text-white/45 text-xs">Total Projects</p>
        </div>
        <div className={`${panel} p-6`}>
          <p className="text-xl font-semibold text-white/90">
            {briefs.filter((b) => b.status === 'in_progress').length}
          </p>
          <p className="text-white/45 text-xs">In Progress</p>
        </div>
        <div className={`${panel} p-6`}>
          <p className="text-xl font-semibold text-white/90">
            {briefs.filter((b) => b.status === 'in_review').length}
          </p>
          <p className="text-white/45 text-xs">In Review</p>
        </div>
        <div className={`${panel} p-6`}>
          <p className="text-xl font-semibold text-white/90">
            {briefs.filter((b) => b.status === 'completed').length}
          </p>
          <p className="text-white/45 text-xs">Completed</p>
        </div>
      </div>

      {/* Briefs List */}
      <div className={`${panel} p-6`}>
        <h3 className={`${headingClass} mb-4`}>Assigned Projects ({briefs.length})</h3>
        {briefs.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="mx-auto mb-4 text-white/30" size={32} />
            <p className={mutedClass}>No projects assigned yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {briefs.map((brief) => (
              <div
                key={brief.id}
                className={`${panelInner} p-4 border-l-2 border-l-white/20 cursor-pointer hover:bg-white/[0.04] transition-colors`}
                onClick={() => setSelectedBriefId(brief.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium text-sm text-white/90 mb-1">
                      {brief.objective || 'Untitled Brief'}
                    </h4>
                    {brief.reference && (
                      <p className="text-xs text-white/40 mb-1 font-mono">
                        Reference: <span className="text-white/60">{brief.reference}</span>
                      </p>
                    )}
                    <p className="text-xs text-white/45 mb-2">
                      {brief.companyProfile?.companyName || brief.user.companyName || 'No company'} •{' '}
                      {brief.companyProfile?.industry || 'N/A'} • Client: {brief.user.username}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-white/40">
                      <span>Created: {new Date(brief.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span
                      className={`px-2.5 py-0.5 text-[10px] font-medium rounded-md border ${
                        brief.status === 'submitted'
                          ? 'bg-blue-500/10 text-blue-300/90 border-blue-500/20'
                          : brief.status === 'in_progress'
                          ? 'bg-yellow-500/10 text-yellow-300/90 border-yellow-500/20'
                          : brief.status === 'in_review'
                          ? 'bg-purple-500/10 text-purple-300/90 border-purple-500/20'
                          : brief.status === 'completed'
                          ? 'bg-emerald-500/10 text-emerald-300/90 border-emerald-500/20'
                          : 'bg-white/5 text-white/50 border-white/10'
                      }`}
                    >
                      {brief.status}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setSelectedBriefId(brief.id)
                      }}
                      className="text-white/45 hover:text-white/85"
                    >
                      <Eye size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
